import { defineComponent as _defineComponent } from 'vue'
import { Resolution } from '@/modules/shared/types';
import Utils from '@/assets/js/utils/Utils';
import { Port } from '@/modules/ctx-dashboard/Port';
import type { Dashboard, Portfolio, WidgetConfig } from '@/modules/ctx-dashboard';
import { WidgetType } from '@/modules/ctx-dashboard';
import BaseDialog from '@/modules/shared/components/dialogs/BaseDialog.vue';
import LoadingAnimationSmall from '@/modules/shared/components/loading-animation/LoadingAnimationSmall.vue';
import { WidgetStepUtils } from '@/components/widgets';
import { WidgetWizardTab } from '@/components/widget-wizard/types/WidgetWizardTab';
import { ref, withDefaults, onMounted, watch, computed, nextTick, Ref } from 'vue';
import { WizardTab } from '@/components/widget-wizard';
import PresetSelection from './PresetSelection.vue';
import type { WidgetWizardStepConfig } from '../types/WidgetWizardStep';

interface WidgetWizardStep<P = any> extends WidgetWizardStepConfig<P> {
    valid?: boolean;
    enabled?: boolean;
}

type Props = {
    value?: WidgetConfig | null;
    entrypoint?: WidgetWizardTab | null;
    entrypointAction?: string | null;
    portfolioKey?: string | null;
    dashboardKey?: string | null;
}
type Emits = {
    (e: 'submit', widget: WidgetConfig): void;
    (e: 'close'): void;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'WidgetWizard',
  props: {
    value: { default: null },
    entrypoint: { default: null },
    entrypointAction: { default: null },
    portfolioKey: { default: null },
    dashboardKey: { default: null }
  },
  emits: ["submit", "close"],
  setup(__props: any, { emit: emits }: { emit: ({
    (e: 'submit', widget: WidgetConfig): void;
    (e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: WidgetConfig | null, entrypoint: WidgetWizardTab | null, entrypointAction: string | null, portfolioKey: string | null, dashboardKey: string | null };




const disableOneClickAdd = ref<boolean>(false);
const widget = ref<WidgetConfig>(createDefaultWidget());
const skipValidWizardTabs = ref<boolean>(false);
const wizard = ref<boolean>(false);
const action = ref<string|null>(null);
const stepsLoading = ref<Promise<void>|null>(null);
const steps = ref<WidgetWizardStep[]>([]);
const activeStep = ref<number>(0);
const loading = ref<boolean>(false);
const dashboard = ref<Dashboard|null>(null);
const portfolio = ref<Portfolio|null>(null);
const activeStepComponentRef = ref<WizardTab|null>(null);

const invalidTabs = computed<string[]>(() => steps.value
    // step.valid may be undefined, which is ok for tabs without validation or invisible tabs
    .filter((step) => step.valid === false)
    .map((step) => step.name));

const currentTab = computed<WidgetWizardStep|undefined>(() => steps.value[activeStep.value]);
const currentTabValid = computed<boolean>(() => currentTab.value?.valid !== false);
const allTabsValid = computed<boolean>(() => invalidTabs.value.length === 0);
const tabs = computed<{ [key: string]: any }>(() => {
    const t: { [key: string]: any } = {};
    t[WidgetWizardTab.Metrics] = () => import('./tabs/metrics/TabMetrics.vue');
    t[WidgetWizardTab.Axis] = () => import('./tabs/axis/TabAxis.vue');
    t[WidgetWizardTab.Data] = () => import('./tabs/data/TabData.vue');
    t[WidgetWizardTab.Resolution] = () => import('./tabs/resolution/TabResolution.vue');
    t[WidgetWizardTab.AggregationTime] = () => import('./tabs/aggregation-time/TabAggregationTime.vue');
    t[WidgetWizardTab.AggregationDatasources] = () => import('./tabs/aggregation-datasources/TabAggregationDatasources.vue');
    t[WidgetWizardTab.Time] = () => import('./tabs/time/TabTime.vue');
    t[WidgetWizardTab.General] = () => import('./tabs/general/TabGeneral.vue');
    t[WidgetWizardTab.Availability] = () => import('./tabs/availability/TabAvailability.vue');
    t[WidgetWizardTab.Commissions] = () => import('./tabs/commissions/TabCommissions.vue');
    t[WidgetWizardTab.Distribution] = () => import('./tabs/distribution/TabDistribution.vue');
    t[WidgetWizardTab.TimeNumber] = () => import('./tabs/time-number/TabTimeNumber.vue');
    t[WidgetWizardTab.PowerCurve] = () => import('./tabs/powercurve/TabPowerCurve.vue');
    t[WidgetWizardTab.LogEvents] = () => import('./tabs/log-events/TabLogEvents.vue');
    t[WidgetWizardTab.Note] = () => import('./tabs/note/TabNote.vue');
    t[WidgetWizardTab.OperatorForecast] = () => import('./tabs/operator-forecast/TabOperatorForecast.vue');
    t[WidgetWizardTab.Map] = () => import('./tabs/map/TabMap.vue');
    t[WidgetWizardTab.Table] = () => import('./tabs/table/TabTable.vue');
    return t;
});

async function fetchPortfolio(): Promise<void> {
    const key = widget.value.portfolioKey;
    portfolio.value = await Port.portfolios.getPortfolio(key);
}
async function fetchDashboard(): Promise<void> {
    const key = widget.value.dashboardKey;
    dashboard.value = await Port.dashboards.getDashboard(key);
}
async function onActiveStepChanged(): Promise<void> {
    for (let i = 0; i <= activeStep.value; i++) {
        const step = steps.value[i];
        if (!step.enabled) {
            step.enabled = true;
            steps.value[i] = step;
        }
    }
    await initCurrentTab();
}
async function awaitLoadingSteps(): Promise<void> {
    if (stepsLoading.value) {
        await stepsLoading.value;
        await Utils.wait(10);
    }
}
async function back(): Promise<void> {
    await awaitLoadingSteps();
    let i = activeStep.value;
    i--;
    while (!isStepVisible(i)) {
        // tab is not visible or not enabled
        i--;
    }
    const minimumStep = wizard.value ? -1 : 0;
    activeStep.value = Math.max(i, minimumStep);
}
async function startWizard(): Promise<void> {
    if (skipValidWizardTabs.value) {
        await awaitLoadingSteps();
        let i = activeStep.value;
        i++;
        while (!isStepVisible(i) || steps.value[i].valid) {
            // tab is not visible or not enabled
            i++;
        }
        const maximumStep = steps.value.length - 1;
        activeStep.value = Math.min(i, maximumStep);
    } else {
        await next();
    }
}
async function next(): Promise<void> {
    await awaitLoadingSteps();
    let i = activeStep.value;
    i++;
    while (!isStepVisible(i)) {
        // tab is not visible or not enabled
        i++;
    }
    const maximumStep = steps.value.length - 1;
    activeStep.value = Math.min(i, maximumStep);
}
function isStepVisible(index: number): boolean {
    if (index === -1) {
        // preset selection
        return true;
    }
    const step = steps.value[index];
    if (!step) {
        // possibly loading
        return true;
    }
    return !step.visible || step.visible(widget.value);
}
async function jumpToEntrypoint(): Promise<void> {
    await awaitLoadingSteps();
    if (props.entrypoint) {
        const tabFound = await jumpToTab(props.entrypoint);
        if (!tabFound && props.entrypoint === WidgetWizardTab.Time) {
            await jumpToTab(WidgetWizardTab.TimeNumber);
        }
    }
    nextTick(initCurrentTab);
}
async function jumpToTab(tab: WidgetWizardTab): Promise<boolean> {
    await awaitLoadingSteps();
    const index = steps.value.findIndex((step) => step.tab === tab);
    const step = steps.value[index];
    if (step) {
        if (step.visible !== undefined && !step.visible(widget.value)) {
            return false;
        }
        if (!step.enabled) {
            return false;
        }
        jump(index);
        return true;
    }
    return false;
}
function jump(step: number) {
    if (steps.value[step].enabled) {
        activeStep.value = step;
    }
}
async function setWidgetFromPreset(widgetConfig: WidgetConfig|null): Promise<void> {
    widget.value = widgetConfig || createDefaultWidget();
    stepsLoading.value = loadSteps(widget.value);
    await stepsLoading.value;
}
async function loadSteps(widgetConfig: WidgetConfig): Promise<void> {
    steps.value = [];
    const userLicenseFeatures = await Port.user.getUserLicenseFeatures();
    steps.value = WidgetStepUtils.getWizardStepsForWidgetType(widgetConfig.type)
        .filter((step) => !step.features || step.features.length === 0
            || !step.features.find((feature) => !userLicenseFeatures.includes(feature)))
        .map((step, index) => ({
            ...step,
            valid: undefined,
            enabled: wizard.value && index > activeStep.value ? undefined : true,
        }));
}
function updateValidation(index: number, valid: boolean) {
    const step = steps.value[index];
    step.valid = valid;
    steps.value[index] = step;
    // debug validation over all tabs
    // if (!valid) {
    //     console.log(`${step.name} is invalid!`);
    // }
}
async function initCurrentTab(tryCounter: number = 0) {
    const component = await getActiveComponent();
    if (component) {
        component.onShown();
    } else if (tryCounter < 10) {
        setTimeout(() => initCurrentTab(tryCounter + 1), 10);
    }
}
async function getActiveComponent(): Promise<WizardTab|undefined> {
    // wait for activeStepComponentRef to be set
    await nextTick();
    let componentRef = activeStepComponentRef.value as WizardTab|null;
    if (!componentRef) {
        // error shows a few times while loading the components, so just ignore
        // console.error(`component with name ${steps.value[activeStep.value].name} not defined`);
        return undefined;
    }
    if (Array.isArray(componentRef) && componentRef.length > 0) {
        componentRef = componentRef[0];
    }
    if (!componentRef?.onShown) {
        console.error(`component with name ${steps.value[activeStep.value].name} has no onShown function`);
        return undefined;
    }
    return componentRef;
}
/**
 * Creates an empty default widget, to prevent null errors, this is not intended to be actually used!
 */
function createDefaultWidget(): WidgetConfig {
    return {
        key: '',
        dashboardKey: props.dashboardKey || '',
        portfolioKey: props.portfolioKey || '',
        order: 0,
        preset: '',
        config: {},
        intervalName: '1M',
        intervalTo: new Date(),
        intervalFrom: new Date(),
        resolution: Resolution.Min10,
        title: '',
        generatedTitle: '',
        type: WidgetType.TimeChart,
        axis: [],
    };
}
function save() {
    loading.value = true;
    emits('submit', widget.value);
}

function cancel() {
    emits('close');
}

watch(() => widget.value.portfolioKey, fetchPortfolio);
watch(() => widget.value.dashboardKey, fetchPortfolio);
watch(() => activeStep.value, onActiveStepChanged);

onMounted(async () => {
    widget.value = Utils.deepCopy(props.value || createDefaultWidget());
    wizard.value = props.value === null;
    activeStep.value = wizard.value ? -1 : 0;
    action.value = props.entrypointAction;

    await fetchDashboard();
    await fetchPortfolio();
    await setWidgetFromPreset(widget.value);
    await jumpToEntrypoint();
});

return { __sfc: true,props, emits, disableOneClickAdd, widget, skipValidWizardTabs, wizard, action, stepsLoading, steps, activeStep, loading, dashboard, portfolio, activeStepComponentRef, invalidTabs, currentTab, currentTabValid, allTabsValid, tabs, fetchPortfolio, fetchDashboard, onActiveStepChanged, awaitLoadingSteps, back, startWizard, next, isStepVisible, jumpToEntrypoint, jumpToTab, jump, setWidgetFromPreset, loadSteps, updateValidation, initCurrentTab, getActiveComponent, createDefaultWidget, save, cancel, BaseDialog, LoadingAnimationSmall, PresetSelection }
}

})